import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ShoppingBag, Plus, Search, Loader2, Car, Calendar, Tag, User,
  ChevronRight, Filter, Eye, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarketplacePage() {
  const [listings, setListings] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('shrt_token');

  const activeType = searchParams.get('type') || '';
  const activeBrand = searchParams.get('brand') || '';
  const activeSearch = searchParams.get('q') || '';

  useEffect(() => { fetchListings(); }, [activeType, activeBrand, activeSearch]);

  const fetchListings = async () => {
    try {
      const params = new URLSearchParams();
      if (activeType) params.set('type', activeType);
      if (activeBrand) params.set('brand', activeBrand);
      if (activeSearch) params.set('search', activeSearch);
      const res = await axios.get(`${API_URL}/api/marketplace/listings?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setListings(res.data.listings || []);
      setBrands(res.data.brands || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const setFilter = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const formatPrice = (listing) => {
    if (listing.type === 'search') {
      return listing.budget_max ? `Budget: CHF ${listing.budget_max.toLocaleString('de-CH')}` : '';
    }
    if (listing.price_on_request || listing.price == null) return 'Preis auf Anfrage';
    return `CHF ${listing.price.toLocaleString('de-CH')}`;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-['Oswald'] text-3xl font-bold uppercase tracking-wide flex items-center gap-3">
              <ShoppingBag className="text-primary" /> Marktplatz
            </h1>
            <p className="text-muted-foreground mt-1">{listings.length} aktive Inserate</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/marketplace/new?type=sell')} className="rounded-none uppercase tracking-wider" data-testid="create-sell-btn">
              <Plus className="w-4 h-4 mr-2" /> Verkaufen
            </Button>
            <Button onClick={() => navigate('/marketplace/new?type=search')} variant="outline" className="rounded-none uppercase tracking-wider" data-testid="create-search-btn">
              <Search className="w-4 h-4 mr-2" /> Suche aufgeben
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={activeSearch}
              onChange={(e) => setFilter('q', e.target.value)}
              placeholder="Suche nach Marke, Modell..."
              className="pl-10 rounded-none"
              data-testid="marketplace-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter('type', '')}
              className={`px-3 py-1.5 text-sm border transition-colors ${!activeType ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
              data-testid="filter-all"
            >
              Alle
            </button>
            <button
              onClick={() => setFilter('type', 'sell')}
              className={`px-3 py-1.5 text-sm border transition-colors ${activeType === 'sell' ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
              data-testid="filter-sell"
            >
              Zu verkaufen
            </button>
            <button
              onClick={() => setFilter('type', 'search')}
              className={`px-3 py-1.5 text-sm border transition-colors ${activeType === 'search' ? 'border-blue-500 bg-blue-500/10 text-blue-500' : 'border-border text-muted-foreground hover:border-blue-500/50'}`}
              data-testid="filter-search"
            >
              Gesuche
            </button>
          </div>
          {brands.length > 0 && (
            <select
              value={activeBrand}
              onChange={(e) => setFilter('brand', e.target.value)}
              className="bg-background border border-input px-3 py-1.5 text-sm focus:outline-none"
              data-testid="filter-brand"
            >
              <option value="">Alle Marken</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border">
            <Car className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">Keine Inserate gefunden</p>
            {(activeSearch || activeType || activeBrand) && (
              <Button variant="link" onClick={() => setSearchParams({})} className="mt-2">Filter zurücksetzen</Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => (
              <Link
                key={listing.id}
                to={`/marketplace/${listing.id}`}
                className="bg-card border border-border overflow-hidden hover:border-primary/50 transition-colors group flex flex-col"
                data-testid={`listing-card-${listing.id}`}
              >
                {/* Type badge + Image */}
                <div className="relative">
                  {listing.images?.length > 0 ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={listing.images[0].url} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center">
                      <Car className="w-16 h-16 text-muted-foreground/30" />
                    </div>
                  )}
                  <span className={`absolute top-2 left-2 text-xs font-bold uppercase tracking-wider px-2 py-0.5 ${listing.status === 'sold' ? 'bg-green-600 text-white' : listing.type === 'sell' ? 'bg-primary text-white' : 'bg-blue-500 text-white'}`}>
                    {listing.status === 'sold' ? 'Verkauft' : listing.type === 'sell' ? 'Verkauf' : 'Suche'}
                  </span>
                </div>

                {/* Info */}
                <div className="p-4 flex-1">
                  <h3 className="font-semibold text-foreground line-clamp-1">{listing.title}</h3>
                  {listing.year && <p className="text-sm text-primary mt-0.5"><Calendar className="w-3 h-3 inline mr-1" />{listing.year}</p>}
                  {listing.type === 'search' && listing.year_from && (
                    <p className="text-sm text-blue-500 mt-0.5"><Calendar className="w-3 h-3 inline mr-1" />{listing.year_from}–{listing.year_to || '...'}</p>
                  )}
                  {listing.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{listing.description}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border mt-auto">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0">
                    <User className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{listing.member_name}</span>
                  </div>
                  <span className={`text-sm font-semibold flex-shrink-0 ${listing.type === 'search' ? 'text-blue-500' : 'text-primary'}`}>
                    {formatPrice(listing)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
